import React, { useEffect, useRef, useState } from 'react';
import { useChatWidgetContext } from '../../ChatWidgetContext';
import { ChatMessage } from '../../types';
import MessageBubble from './MessageBubble';
import { ChatMarkdownContent } from './ChatMarkdownContent';
import TypingIndicator from './TypingIndicator';
import styles from '../ChatWidget.module.scss';
import { LlmTokenUsage, useDateHelper, useLlmTokenUsageService } from 'nekst-api';
import nekstAiIcon from './nekstAiIcon.png';
import { useDataLoaderNew } from 'data-loader';

function useTypewriterText(fullText: string, charsPerTick = 2, intervalMs = 16): string {
  const [displayed, setDisplayed] = useState('');
  const indexRef = useRef(0);

  useEffect(() => {
    if (!fullText) {
      setDisplayed('');
      indexRef.current = 0;
      return;
    }

    if (indexRef.current >= fullText.length) {
      setDisplayed(fullText);
      return;
    }

    const timer = setInterval(() => {
      indexRef.current = Math.min(indexRef.current + charsPerTick, fullText.length);
      setDisplayed(fullText.slice(0, indexRef.current));

      if (indexRef.current >= fullText.length) {
        clearInterval(timer);
      }
    }, intervalMs);

    return () => clearInterval(timer);
  }, [fullText, charsPerTick, intervalMs]);

  return displayed;
}

function StreamingBubble(props: { text: string; isGrouped: boolean }) {
  const displayedText = useTypewriterText(props.text);

  const streamingMessage: ChatMessage = {
    role: 'assistant',
    content: displayedText,
    timestamp: new Date(),
  };

  return <MessageBubble message={streamingMessage} isGrouped={props.isGrouped} />;
}

function isAssistantSide(role: string): boolean {
  return role === 'assistant' || role === 'tool-activity';
}

const WELCOME_TEXT = `Hi! I'm **Nekst AI**, your real estate transaction assistant. Here's how I can help:

- **Transactions** — look up details, tasks, notes, and history
- **Contacts & people** — search contacts, view team members and their activity
- **Tasks** — check assignments, due dates, and overdue key dates

Just type your question below to get started!`;

function isFreePlan(planId: string | null): boolean {
  return !planId || planId === 'FREE_MONTHLY';
}

function UsageLimitWarning(props: { usage: LlmTokenUsage }) {
  const { usage } = props;
  const dateHelper = useDateHelper();

  if (usage.percentUsed >= 100) {
    const resetDate = dateHelper.toString(usage.periodEnd);

    if (isFreePlan(usage.planId)) {
      return (
        <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
          <div className={styles.bubbleAvatarSpacer} />
          <div className={styles.bubbleWrapper}>
            <div className={styles.limitReachedBubble}>
              Your team has reached its monthly AI chat limit. The limit resets on {resetDate}. Consider upgrading your plan to increase the limit.
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
          <div className={styles.bubbleAvatarSpacer} />
          <div className={styles.bubbleWrapper}>
            <div className={styles.limitReachedBubble}>
              Your team has reached its monthly AI chat limit. The limit resets on {resetDate}. Contact support for additional capacity.
            </div>
          </div>
        </div>
      );
    }
  } else if (usage.percentUsed >= 80) {
    return (
      <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
        <div className={styles.bubbleAvatarSpacer} />
        <div className={styles.bubbleWrapper}>
          <div className={styles.warningBubble}>
            Your team has used {Math.round(usage.percentUsed)}% of its monthly AI chat limit. Contact support if you need additional capacity.
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
}

function WelcomeMessage() {
  return (
    <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
      <div className={styles.bubbleAvatar}>
        <img src={nekstAiIcon} alt="Nekst AI" width={28} height={28} />
      </div>
      <div className={styles.bubbleWrapper}>
        <div className={styles.bubbleMeta}>
          <span className={styles.bubbleSender}>Nekst AI</span>
        </div>
        <div className={styles.assistantBubble}>
          <ChatMarkdownContent content={WELCOME_TEXT} />
        </div>
      </div>
    </div>
  );
}

export default function MessageList() {
  const {
    messages,
    streamingText,
    isStreaming,
    isToolExecuting,
  } = useChatWidgetContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  const usageService = useLlmTokenUsageService();
  const { data: usageData } = useDataLoaderNew(usageService.getUsage);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, streamingText]);

  const showTypingIndicator = isStreaming && !streamingText && !isToolExecuting;
  const lastMessage = messages[messages.length - 1];
  const streamingIsGrouped = !!lastMessage && isAssistantSide(lastMessage.role);

  const lastToolActivityIndex = messages.reduce<number>(
    (acc, msg, i) => (msg.role === 'tool-activity' ? i : acc),
    -1,
  );

  const showWelcome = messages.length === 0 && !isStreaming;

  return (
    <div className={styles.messageList} ref={scrollRef} data-testid="chat-message-list">
      <div className={styles.dateSeparator}>Today</div>
      {showWelcome && <WelcomeMessage />}
      {showWelcome && usageData && <UsageLimitWarning usage={usageData} />}
      {messages.map((message, index) => {
        const prev = index > 0 ? messages[index - 1] : undefined;
        const grouped = !!prev
          && isAssistantSide(message.role)
          && isAssistantSide(prev.role);
        const isActiveToolCall = isToolExecuting
          && message.role === 'tool-activity'
          && index === lastToolActivityIndex;
        return (
          <MessageBubble
            key={index}
            message={message}
            isGrouped={grouped}
            isActiveToolCall={isActiveToolCall}
          />
        );
      })}
      {showTypingIndicator && <TypingIndicator />}
      {streamingText && <StreamingBubble text={streamingText} isGrouped={streamingIsGrouped} />}
    </div>
  );
}
